import type { CompRef } from 'types/documentServices';
import type { LayoutApiByStructure } from '../createLayoutApi_structure';
import type { LayoutGetApi } from '../layoutGetApi';
import type { HistoryAddOptions, HistoryApi } from '../createHistoryApi';
import type { EditorAPI } from '#packages/editorAPI';
import type { LayoutMoveByOptions, LayoutMoveByParams } from './types';

export function createLayoutMoveApi_structure({
  editorAPI,
  historyApi,
  layoutGetApi,
  layoutApi_structure,
}: {
  editorAPI: EditorAPI;
  historyApi: HistoryApi;
  layoutGetApi: LayoutGetApi;
  layoutApi_structure: LayoutApiByStructure;
}) {
  function moveBy(
    compRefOrRefs: CompRef | CompRef[],
    { deltaX, deltaY }: LayoutMoveByParams,
    options: LayoutMoveByOptions = {},
  ) {
    const currentLayout = layoutGetApi.getRelativeToStructure(compRefOrRefs);

    const x =
      typeof deltaX === 'number' ? currentLayout.x + deltaX : currentLayout.x;
    const y =
      typeof deltaY === 'number' ? currentLayout.y + deltaY : currentLayout.y;

    layoutApi_structure.updateRelativeToStructure(
      compRefOrRefs,
      { x, y },
      options.dontAddToUndoRedoStack,
    );
  }

  function moveByAndPushTransition(compRef: CompRef) {
    const compLayoutInitial =
      editorAPI.components.layout.measure.getBoundingLayoutRelativeToScreen(
        compRef,
      );

    editorAPI.components.layout.setLayoutReadFromDOM(true);
    editorAPI.dsActions.renderPlugins.setCompsToShowOnTop([compRef.id]);

    // eslint-disable-next-line @wix/santa-editor/deprecatedDSApi
    editorAPI.components.layout.updateAndPushStart(compRef);

    const applyDeltaCorrectionIfNeeded = (compRef: CompRef, deltaY: number) => {
      // TODO: Investigate the issue of additional space appearing when moving the gap above the rotated component.
      // https://jira.wixpress.com/browse/WEED-32133
      return editorAPI.components.layout.isRotated(compRef)
        ? deltaY - 1
        : deltaY;
    };

    const update = ({ deltaY }: { deltaY: number }) => {
      editorAPI.components.layout.dragAndPush(compRef, {
        y: applyDeltaCorrectionIfNeeded(compRef, compLayoutInitial.y + deltaY),
      });
    };

    const end = async (options: HistoryAddOptions & {} = {}) => {
      // eslint-disable-next-line @wix/santa-editor/deprecatedDSApi
      editorAPI.components.layout.updateAndPushEnd(compRef);
      historyApi.add('component - move and push', options);
      editorAPI.components.layout.setLayoutReadFromDOM(false);
      editorAPI.dsActions.renderPlugins.setCompsToShowOnTop(null);
    };

    return {
      update,
      end,
    };
  }

  return {
    moveByAndPushTransition,
    moveBy,
  };
}

export type LayoutMoveApiByStructure = ReturnType<
  typeof createLayoutMoveApi_structure
>;
